import { inject, Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs/operators';
import swal from 'sweetalert2';
import { CookieService } from 'ngx-cookie-service';
import { loadFarmWeatherLocations } from '../_actions/farmWeatherLocation';
import { PostService } from './cms/post.service';
import { UiMode } from '../../../assets/data/enum/uiMode';
import { GeneralUtilsService } from './common/generalUtils.service';
import { CropSubUnitService } from './cropSubUnit.service';
import { loadMachineActivities } from '../_actions/machineActivity';
import { StoreModelService } from './storeModel.service';
import { SatelliteService } from './satellite/satellite.service';
import { BatchService } from './batch.service';
import { CropMetricsService } from './cropMetrics.service';
import { loadCustomers } from '../_actions/customer';
import { loadEmployees } from '../_actions/employee';
import { loadCompanyContacts } from '../_actions/companyContact';
import { loadSuppliers } from '../_actions/supplier';
import { SM } from '../../../assets/data/enum/storeModel.enum';
import { AppState } from '../_reducers';
import { loadCommodities } from '../_actions/commodity';
import { loadEquipments } from '../_actions/equipment';
import { loadFarmCadastralSheets } from '../_actions/farmCadastralSheet';
import { loadFieldGroups } from '../_actions/fieldGroup';
import { loadUserGroups } from '../_actions/userGroup';
import { setCurrentUserSuccess } from '../_actions/currentUser';
import { loadCurrentFarm } from '../_actions/currentFarm';
import { loadUsers } from '../_actions/user';
import { environment } from '../../../environments/environment';
import { loadEquipmentActivities } from '../_actions/equipmentActivity';
import { loadDevices } from '../_actions/device';
import { loadContainers } from '../_actions/container';
import { loadWarehouses } from '../_actions/warehouse';
import { loadLoads } from '../_actions/load';
import { loadLandRegistries } from '../_actions/landRegistry';
import { FarmService } from './farm.service';
import { UserService } from './user.service';
import { loadCompanies } from '../_actions/company';
import { ThemeConfig } from '../../../assets/data/enum/themeConfig';
import { LegalsService } from './legals.service';
import { TelemetryConfigurationService } from './telemetryConfiguration.service';
import { InsectsZoneService } from './insects/insectsZone.service';
import { ProductService } from './product.service';
import { Farm, User } from '../_models';
import { loadNotes } from '../_actions/note';
import { loadCompanyAddresses } from '../_actions/companyAddress';
import { LocaleService } from './locale.service';
import { loadPayments } from '../_actions/payment';
import { XsurveyService } from './xsurvey.service';
import { SAASBRANDNAME } from '../../../assets/data/enum/saasBrandName.enum';
import { CommonService } from './common.service';
import { loadParcels } from '../_actions/parcel';
import { loadFields } from '../_actions/field';
import { selectFieldWithStatus } from '../_selectors/field.selectors';
import { selectStoreModelsWithStatus } from '../_selectors/storeModel.selectors';
import { XYearService } from './xYear.service';
import { loadFieldActivities } from '../_actions/fieldActivity';
import { loadContainerActivities } from '../_actions/containerActivity';

@Injectable()
export class DataService implements OnDestroy {
  // Services
  private store = inject(Store<AppState>);
  private farmService = inject(FarmService);
  private userService = inject(UserService);
  private cropMetricsService = inject(CropMetricsService);
  private batchService = inject(BatchService);
  private satelliteService = inject(SatelliteService);
  private storeModelService = inject(StoreModelService);
  private cropSubUnitService = inject(CropSubUnitService);
  private telemetryConfigurationService = inject(TelemetryConfigurationService);
  private translate = inject(TranslateService);
  private generalUtilsService = inject(GeneralUtilsService);
  private legalsService = inject(LegalsService);
  private insectsZoneService = inject(InsectsZoneService);
  private productService = inject(ProductService);
  private postService = inject(PostService);
  private localeService = inject(LocaleService);
  private xsurveyService = inject(XsurveyService);
  private commonService = inject(CommonService);
  private cookieService = inject(CookieService);
  private xYearService = inject(XYearService);

  private year: number;
  private subscriptions: Subscription = new Subscription();
  brand = SAASBRANDNAME;

  constructor() {}

  async getInitialData() {
    //  first we need to get the user
    const user: User = await this.userService.getUser().toPromise();

    //  If user is valid
    if (user && user.id) {
      //  Save the user in the store
      this.store.dispatch(setCurrentUserSuccess({ user }));

      //  Set the language if preferred language is present
      if (user.preferredLanguage) {
        this.translate.use(user.preferredLanguage == 'br' ? 'pt_BR' : user.preferredLanguage);
        this.localeService.setLocale(
          user.preferredLanguage == 'br' ? 'pt_BR' : user.preferredLanguage
        );
        localStorage.setItem(
          'crntLng',
          user.preferredLanguage == 'br' ? 'pt_BR' : user.preferredLanguage
        );
      }
      if (environment.enableTranslateMode) {
        const pseudoLanguage = 'ach';
        this.translate.use(pseudoLanguage);
        localStorage.setItem('crntLng', pseudoLanguage);
      }

      //  Setting user preferred mode if present
      if (user.preferredMode) {
        this.generalUtilsService.updateGlobalUIMode(user.preferredMode);
      } else {
        this.generalUtilsService.updateGlobalUIMode(UiMode.FARM);
      }

      // Setting the year if user has a default year

      this.year =
        user.yearDefault && user.yearDefault > 0 ? user.yearDefault : new Date().getFullYear();
      this.xYearService.updateYear(this.year);

      //  Setting welcome preferences
      if (!localStorage.getItem('welUser') || localStorage.getItem('welUser') === undefined) {
        localStorage.setItem('welUser', JSON.stringify(user.welcomeCheck));
      }

      // Store Models - Loaded once on store Model guard
      // Await for store models to be loaded
      const { storeModels } = await firstValueFrom(
        this.store.select(selectStoreModelsWithStatus).pipe(
          filter(({ loaded }) => loaded),
          take(1)
        )
      );

      if (storeModels && storeModels.length > 0) {
        //  Refreshing the current farm
        this.store.dispatch(loadCurrentFarm({ forceReload: true }));

        //  Get all the farms
        //  purposely as a promise to make this api call blocking and easy to read
        let farms: Farm[] = [];
        if (ThemeConfig.isAnalyticsMode != true) {
          farms = await this.farmService.getList().toPromise();
        } else {
          this.farmService.getList().subscribe();
        }

        if (ThemeConfig.brand === this.brand.HUBFARM) {
          //Looking for a CUAA in the cookies to select the correct farm in case of Hubfarm
          const cookieCuaa = this.cookieService.get('sso_attr');
          let farmFromCookie = null;
          // If the cookie is present we check if the farm is present in the list of farms
          if (cookieCuaa) {
            farmFromCookie = farms.find(
              (f: any) =>
                f.externalResources &&
                f.externalResources.find(
                  (er: any) => er.externalSource == 'HUBFARM' && er.externalId == cookieCuaa
                )
            );
          }

          // If the farm is present we set it as the current farm
          if (farmFromCookie) {
            localStorage.setItem('companySelected', JSON.stringify(farmFromCookie));
            this.store.dispatch(loadCurrentFarm({ forceReload: true }));
            // If the farm is not present we remove the cookie and show an alert
          } else {
            localStorage.removeItem('companySelected');
            swal.fire(
              this.translate.instant('alert_wrong_cuaa.title', {}),
              this.translate.instant('alert_wrong_cuaa.subtitle', {}),
              'warning'
            );
          }

          // Removing the cookie at the operations end
          // this.cookieService.delete('sso_attr');
        }

        // Getting the current company from the local storage
        let currentCompany = JSON.parse(localStorage.getItem('companySelected'));
        const companyLoggedIn = localStorage.getItem('companyLoggedIn');

        //  IF currentCompany is not present in the local storage
        //  then get it by matching farms and current user id
        if (!currentCompany || !companyLoggedIn) {
          currentCompany = farms.find((farm) => farm.id === user.farm.id);

          //  Setting the current company in the local storage
          localStorage.setItem('companySelected', JSON.stringify(currentCompany));
          localStorage.setItem('companyLoggedIn', currentCompany.id);
        }
        const lng = this.translate.currentLang.toString();
        // Getting legal options if needed
        if (ThemeConfig.legalModeBySaas) {
          const options = await this.legalsService.getLegalTemplateOption(lng).toPromise();

          if (user.legalLog && options) {
            // checks if the user has to complete the legal options before proceeding
            this.legalsService.checkLegalOptionFromUser(options, user.legalLog);
          }
        }

        if (currentCompany) {
          try {
            localStorage.setItem('tmpbackp', '2');

            //  If telemetry is enabled then get the configuration
            if (
              this.storeModelService.enableActionByStoreModelAndPermission(
                SM.SM_EQUIPMENT_TELEMETRY
              ).read
            ) {
              this.subscriptions.add(
                this.telemetryConfigurationService.getTelemetryConfiguration().subscribe()
              );
            }

            // IF we are on livestock we make sure to have permissions to keep the mode
            if (!this.storeModelService.enableActionByStoreModelAndPermission(SM.SM_STABLE).read) {
              this.generalUtilsService.updateGlobalUIMode(UiMode.FARM);
            }

            //  DISPATCHING BLOCKING SERVICES MANDATORY FOR APP STARTUP
            const promises: Promise<any>[] = [
              firstValueFrom(this.cropMetricsService.getCropMetrics()),
            ];

            // Dispatching loadFields and waiting for `loaded: true`
            this.store.dispatch(loadFields({ year: this.year }));

            const fieldsLoaded$ = firstValueFrom(
              this.store.select(selectFieldWithStatus).pipe(
                filter((state) => state.loaded), // Waits until fields are loaded
                take(1) // Completes after the first match
              )
            );

            if (!ThemeConfig.isAnalyticsMode) {
              promises.push(firstValueFrom(this.postService.getTrainingPosts()));
              promises.push(firstValueFrom(this.xsurveyService.getSurveysNumber()));
              promises.push(firstValueFrom(this.commonService.getAndStoreMeasureUnits()));
              promises.push(firstValueFrom(this.commonService.getAndStoreCommonElements()));
            }

            await Promise.all([...promises, fieldsLoaded$]);

            this.store.dispatch(loadFarmWeatherLocations());
            this.store.dispatch(loadCompanies());
            this.store.dispatch(loadCompanyAddresses());
            this.store.dispatch(loadUsers());

            if (ThemeConfig.isAnalyticsMode == true) {
              this.userService.getUserSettings().subscribe();
            }
            this.store.dispatch(loadCommodities());
            this.store.dispatch(loadFieldGroups());
            this.store.dispatch(loadWarehouses());
            this.store.dispatch(loadContainers());
            this.store.dispatch(loadLoads());
            this.productService.getProductCountries().toPromise();
            this.store.dispatch(loadEquipments());
            this.store.dispatch(loadFarmCadastralSheets());
            this.store.dispatch(loadUserGroups());
            this.store.dispatch(loadNotes());
            this.store.dispatch(loadEquipmentActivities());
            this.store.dispatch(loadLandRegistries());
            this.store.dispatch(loadSuppliers());
            this.store.dispatch(loadCompanyContacts());
            this.store.dispatch(loadEmployees());
            this.store.dispatch(loadCustomers());
            this.store.dispatch(loadPayments());
            this.store.dispatch(loadDevices());
            this.store.dispatch(loadFieldActivities({}));
            this.store.dispatch(loadContainerActivities());
            (this.cropSubUnitService.getAllCropSubUnitByYear(this.year).subscribe(),
              this.batchService.getBatches().subscribe());
            this.store.dispatch(loadParcels({ year: this.year }));

            //  Bootstrapping optional data based on permissions
            //  Telemetry
            if (
              this.storeModelService.enableActionByStoreModelAndPermission(
                SM.SM_EQUIPMENT_TELEMETRY
              ).read
            ) {
              this.store.dispatch(loadMachineActivities());
            }

            //  Insects
            if (this.storeModelService.enableActionByStoreModelAndPermission(SM.SM_INSECTS).read) {
              this.subscriptions.add(this.insectsZoneService.getInsectsList().subscribe());
              this.subscriptions.add(this.insectsZoneService.getInsectZones().subscribe());
            }

            //  Setting satellite index provider
            this.satelliteService.setIndexProvider();

            //  Finally resolving the promise
            await Promise.resolve();
          } catch (e) {
            console.error(e);
            await Promise.reject(e);
          }
        } else {
          console.error('Problem with company selected!');
          await Promise.reject('Problem with company selected!');
        }
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
